// App glue: load a PDF, render it, run detection, let the user edit the result,
// then write a real AcroForm out.

import { loadDocument, renderPage, OPS } from './viewer.js';
import { extractPrimitives } from './extract.js';
import { detectFields } from './detect.js';
import { buildFillablePdf, UnsupportedPdfError, inspectPdf } from './build.js';
import { Editor, nextFieldId } from './editor.js';

const MAX_SCALE = 1.5;

const els = {
  file: document.getElementById('file'),
  status: document.getElementById('status'),
  viewer: document.getElementById('viewer'),
  tools: document.getElementById('tools'),
  inspector: document.getElementById('inspector'),
  export: document.getElementById('export'),
  summary: document.getElementById('summary'),
  redetect: document.getElementById('redetect'),
  clear: document.getElementById('clear'),
  download: document.getElementById('download'),
  dropzone: document.getElementById('dropzone'),
  name: document.getElementById('f-name'),
  label: document.getElementById('f-label'),
  type: document.getElementById('f-type'),
  multiline: document.getElementById('f-multiline'),
  del: document.getElementById('f-delete'),
  borders: document.getElementById('opt-borders'),
  flatten: document.getElementById('opt-flatten'),
};

const state = { bytes: null, fileName: 'form.pdf', doc: null, pages: [] };

const editor = new Editor({
  onChange: updateSummary,
  onSelect: showInspector,
});

/* ---------------- loading ---------------- */

els.file.addEventListener('change', (e) => {
  const file = e.target.files?.[0];
  if (file) openFile(file);
});

setupDragAndDrop();

async function openFile(file) {
  if (file.type && file.type !== 'application/pdf') {
    setStatus('That does not look like a PDF.', 'error');
    return;
  }
  setStatus('Reading PDF…');
  try {
    state.bytes = new Uint8Array(await file.arrayBuffer());
    state.fileName = file.name.replace(/\.pdf$/i, '') || 'form';

    const info = await inspectPdf(state.bytes);
    state.doc = await loadDocument(state.bytes);

    await renderAllPages();
    await runDetection();

    els.tools.hidden = false;
    els.export.hidden = false;

    if (info.existingFieldCount > 0) {
      setStatus(
        `Heads up: this PDF already has ${info.existingFieldCount} form field(s). ` +
          `Anything you add here is written alongside them.`,
        'warn',
      );
    }
  } catch (err) {
    console.error(err);
    setStatus(describeError(err), 'error');
  }
}

async function renderAllPages() {
  els.viewer.replaceChildren();
  state.pages = [];

  const available = Math.max(320, els.viewer.clientWidth - 48);
  for (let n = 1; n <= state.doc.numPages; n++) {
    const page = await state.doc.getPage(n);
    const natural = page.getViewport({ scale: 1 });
    const scale = Math.min(MAX_SCALE, available / natural.width);
    const { canvas, viewport } = await renderPage(page, scale);

    const container = document.createElement('div');
    container.className = 'page';
    container.style.width = `${viewport.width}px`;
    container.style.height = `${viewport.height}px`;

    const overlay = document.createElement('div');
    overlay.className = 'overlay';

    container.append(canvas, overlay);

    const wrap = document.createElement('div');
    wrap.className = 'page-wrap';
    const caption = document.createElement('p');
    caption.className = 'page-caption';
    caption.textContent = `Page ${n}`;
    wrap.append(caption, container);
    els.viewer.append(wrap);

    state.pages.push({ pageNumber: n, page, viewport, overlay, fields: [] });
  }
}

async function runDetection() {
  setStatus('Looking for blanks…');
  let imageOnlyPages = 0;

  for (const pageState of state.pages) {
    const primitives = await extractPrimitives(pageState.page, OPS);
    if (primitives.imageDominant) imageOnlyPages += 1;
    pageState.fields = detectFields(primitives).map((f) => ({
      id: nextFieldId(),
      type: f.type,
      name: f.name,
      label: f.label,
      multiline: Boolean(f.multiline),
      rect: f.rect,
    }));
  }

  editor.setPages(state.pages);
  const total = editor.allFields.length;

  if (imageOnlyPages > 0) {
    setStatus(
      `Found ${total} field(s). ${imageOnlyPages} page(s) look like scans — those have no ` +
        `text or line data to read, so draw fields on them by hand.`,
      'warn',
    );
  } else if (total === 0) {
    setStatus('No blanks detected automatically. Drag on the page to add fields.', 'warn');
  } else {
    setStatus(`Found ${total} field(s). Check them over, then export.`);
  }
}

/* ---------------- inspector ---------------- */

function showInspector(found) {
  if (!found) {
    els.inspector.hidden = true;
    return;
  }
  els.inspector.hidden = false;
  els.name.value = found.field.name;
  els.label.value = found.field.label || '';
  els.type.value = found.field.type;
  els.multiline.checked = Boolean(found.field.multiline);
  els.multiline.disabled = found.field.type !== 'text';
}

els.name.addEventListener('input', () => editor.updateSelected({ name: els.name.value }));
els.label.addEventListener('input', () => editor.updateSelected({ label: els.label.value }));
els.type.addEventListener('change', () => {
  editor.updateSelected({ type: els.type.value });
  els.multiline.disabled = els.type.value !== 'text';
});
els.multiline.addEventListener('change', () =>
  editor.updateSelected({ multiline: els.multiline.checked }),
);
els.del.addEventListener('click', () => editor.deleteSelected());

for (const radio of document.querySelectorAll('input[name="newtype"]')) {
  radio.addEventListener('change', () => {
    if (radio.checked) editor.newFieldType = radio.value;
  });
}

els.redetect.addEventListener('click', () => {
  if (state.doc) runDetection();
});
els.clear.addEventListener('click', () => editor.clearAll());

/* ---------------- export ---------------- */

els.download.addEventListener('click', async () => {
  if (!state.bytes) return;
  const pages = state.pages.map((p, index) => ({ pageIndex: index, fields: p.fields }));
  const total = pages.reduce((n, p) => n + p.fields.length, 0);
  if (total === 0) {
    setStatus('Add at least one field before exporting.', 'warn');
    return;
  }

  els.download.disabled = true;
  setStatus('Building the fillable PDF…');
  try {
    const out = await buildFillablePdf(state.bytes, pages, {
      flatten: els.flatten.checked,
      showBorders: els.borders.checked,
    });
    downloadBytes(out, `${state.fileName}-fillable.pdf`);
    setStatus(`Done — wrote ${total} field(s).`);
  } catch (err) {
    console.error(err);
    setStatus(describeError(err), 'error');
  } finally {
    els.download.disabled = false;
  }
});

function downloadBytes(bytes, filename) {
  // Copy into a fresh buffer: pdf-lib may hand back a view onto a larger one.
  const blob = new Blob([bytes.slice()], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/* ---------------- misc ---------------- */

function updateSummary() {
  const fields = editor.allFields;
  const boxes = fields.filter((f) => f.type === 'checkbox').length;
  els.summary.textContent = `${fields.length} field(s): ${fields.length - boxes} text, ${boxes} checkbox.`;

  const names = fields.map((f) => f.name);
  const dupes = names.filter((n, i) => names.indexOf(n) !== i);
  if (dupes.length > 0) {
    els.summary.textContent += ` Duplicate names will be renumbered on export.`;
  }
}

function setStatus(message, kind = 'info') {
  els.status.textContent = message;
  els.status.dataset.kind = kind;
}

function describeError(err) {
  if (err instanceof UnsupportedPdfError) return err.message;
  if (err?.name === 'PasswordException') {
    return 'This PDF is password-protected. Remove the password first, then try again.';
  }
  return `Something went wrong: ${err?.message || err}`;
}

function setupDragAndDrop() {
  let depth = 0;
  window.addEventListener('dragenter', (e) => {
    e.preventDefault();
    depth += 1;
    els.dropzone.hidden = false;
  });
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('dragleave', (e) => {
    e.preventDefault();
    depth = Math.max(0, depth - 1);
    if (depth === 0) els.dropzone.hidden = true;
  });
  window.addEventListener('drop', (e) => {
    e.preventDefault();
    depth = 0;
    els.dropzone.hidden = true;
    const file = e.dataTransfer?.files?.[0];
    if (file) openFile(file);
  });
}
